import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlarmClock,
  ArrowDownRight,
  ArrowUpRight,
  CalendarOff,
  Timer,
  UserCheck,
  UserX,
  Users,
  Wallet,
} from 'lucide-react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { Bone, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import { usePayrollDashboard } from '@/features/payroll/hooks/usePayrollBatches'
import type {
  OrganizationDashboard,
  OrganizationDashboardCompany,
  OrganizationDashboardSummary,
} from '@/types/dashboard.types'
import { DashboardDetailModal } from './DashboardTile'
import type { DashboardView } from './useDashboardView'
import { ATTENDANCE_SERIES } from './chartSeries'
import { formatNumber, formatPct } from './dashboardFormat'

type KpiKey = 'staff' | 'present' | 'late' | 'leave' | 'absent' | 'clocked' | 'payroll'

interface KpiTone {
  icon: string
  accent: string
}

const TONES: Record<KpiKey, KpiTone> = {
  staff: {
    icon: 'bg-primary-purple/10 text-primary-purple dark:bg-indigo-400/15 dark:text-indigo-300',
    accent: 'bg-primary-purple',
  },
  present: {
    icon: 'bg-primary/10 text-primary dark:bg-sky-400/15 dark:text-sky-300',
    accent: 'bg-primary',
  },
  late: {
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
    accent: 'bg-amber-500',
  },
  leave: {
    icon: 'bg-sky-100 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300',
    accent: 'bg-sky-500',
  },
  absent: {
    icon: 'bg-accent/10 text-[#d92d50] dark:bg-rose-400/15 dark:text-rose-300',
    accent: 'bg-accent',
  },
  clocked: {
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
    accent: 'bg-emerald-500',
  },
  payroll: {
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
    accent: 'bg-emerald-500',
  },
}

const ICONS: Record<KpiKey, React.ComponentType<{ className?: string }>> = {
  staff: Users,
  present: UserCheck,
  late: AlarmClock,
  leave: CalendarOff,
  absent: UserX,
  clocked: Timer,
  payroll: Wallet,
}

interface KpiTileModel {
  key: KpiKey
  label: string
  value: string
  foot: React.ReactNode
}

/** People who turned up today, whatever their standing: present, late or half-day. */
function attendedToday(summary: OrganizationDashboardSummary): number {
  return (summary.presentToday ?? 0) + (summary.lateToday ?? 0) + (summary.halfDayToday ?? 0)
}

function shareOf(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '0%'
}

function WeekDelta({
  current,
  previous,
}: {
  current: number | null | undefined
  previous: number | null | undefined
}): React.ReactElement | null {
  if (current == null || previous == null) return null
  const delta = Math.round(current - previous)
  if (delta === 0)
    return <span className="text-gray-500 dark:text-gray-400">level with last week</span>
  const up = delta > 0
  const Icon = up ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 font-medium',
        up ? 'text-emerald-700 dark:text-emerald-400' : 'text-[#d92d50] dark:text-rose-300'
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {up ? '+' : ''}
      {delta} pts vs last week
    </span>
  )
}

function KpiTile({
  model,
  loading,
  onOpen,
}: {
  model: KpiTileModel
  loading: boolean
  onOpen: () => void
}): React.ReactElement {
  const Icon = ICONS[model.key]
  const tone = TONES[model.key]
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${model.label}: ${model.value}. Open details`}
      className={cn(
        'group relative flex min-w-0 flex-col gap-3 overflow-hidden rounded-[var(--radius-panel)] border border-gray-200 bg-white p-4 text-left shadow-[var(--shadow-panel)]',
        'transition-[box-shadow,border-color,transform] duration-150 ease-out hover:-translate-y-px hover:border-gray-300 hover:shadow-[var(--shadow-panel-raised)]',
        'dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none dark:hover:border-gray-600'
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tone.icon)}
        >
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </span>
        <span className="truncate text-[13px] font-medium text-gray-600 dark:text-gray-300">
          {model.label}
        </span>
      </div>
      {loading ? (
        <div className="space-y-2">
          <Bone className="h-7 w-16 rounded-md" tint />
          <Bone className="h-3 w-24 rounded" />
        </div>
      ) : (
        <div className="min-w-0">
          <p className="truncate font-display text-[28px] font-semibold leading-none tracking-[-0.02em] text-gray-900 tabular-nums dark:text-gray-50">
            {model.value}
          </p>
          <p className="mt-2 truncate text-xs text-gray-500 dark:text-gray-400">{model.foot}</p>
        </div>
      )}
      <span
        className={cn(
          'absolute inset-x-4 bottom-0 h-0.5 origin-left scale-x-0 rounded-full transition-transform duration-200 ease-out group-hover:scale-x-100',
          tone.accent
        )}
        aria-hidden
      />
    </button>
  )
}

function usePayrollTile(): { model: KpiTileModel | null; loading: boolean } {
  const { can } = usePermissions()
  const canPayroll = can('payroll', 'read_org')
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const { currency } = useOrgCurrency()
  const payroll = usePayrollDashboard(year, month, { enabled: canPayroll })
  if (!canPayroll) return { model: null, loading: false }
  // A month with nobody in the batch yet reads as "not prepared", not as a zero payroll.
  const preview = payroll.data?.finalizePreview?.totals
  const totals = preview && preview.employeeCount > 0 ? preview : undefined
  const monthName = new Intl.DateTimeFormat(undefined, { month: 'long' }).format(now)
  return {
    loading: payroll.isLoading,
    model: {
      key: 'payroll',
      label: `Payroll · ${monthName}`,
      value: totals
        ? formatCurrency(totals.netPay, currency, { notation: 'compact', maximumFractionDigits: 1 })
        : '—',
      foot: totals
        ? `${formatNumber(totals.employeeCount)} employees · ${formatNumber(totals.companyCount)} companies`
        : 'No payroll prepared yet this month',
    },
  }
}

const KPI_LABELS: Record<Exclude<KpiKey, 'payroll'>, string> = {
  staff: 'Total staff',
  present: 'Present today',
  late: 'Late arrivals',
  leave: 'On leave',
  absent: 'Absent',
  clocked: 'Clocked in now',
}

function buildKpis(data: OrganizationDashboard | undefined): KpiTileModel[] {
  const s = data?.summary
  if (!s) {
    return (['staff', 'present', 'late', 'leave', 'absent', 'clocked'] as const).map((key) => ({
      key,
      label: KPI_LABELS[key],
      value: '0',
      foot: '',
    }))
  }
  const attended = attendedToday(s)
  // A past day: no "right now", and the last tile shows who was scheduled instead.
  const live = data?.isLive ?? true
  return [
    {
      key: 'staff',
      label: KPI_LABELS.staff,
      value: formatNumber(s.activeStaff),
      foot: `across ${formatNumber(s.activeCompanies)} ${s.activeCompanies === 1 ? 'company' : 'companies'}`,
    },
    {
      key: 'present',
      label: live ? KPI_LABELS.present : 'Present',
      value: formatNumber(attended),
      foot:
        s.attendancePct == null ? (
          `of ${formatNumber(s.scheduledToday)} scheduled`
        ) : (
          <>
            <span className={cn('font-medium text-gray-700 dark:text-gray-200')}>
              {formatPct(s.attendancePct)}
            </span>{' '}
            <WeekDelta current={s.attendancePct} previous={s.attendancePctLastWeek} />
          </>
        ),
    },
    {
      key: 'late',
      label: KPI_LABELS.late,
      value: formatNumber(s.lateToday),
      foot:
        s.avgLateMinutesWeek > 0
          ? `${shareOf(s.lateToday ?? 0, attended)} of arrivals · avg ${s.avgLateMinutesWeek}m`
          : `${shareOf(s.lateToday ?? 0, attended)} of arrivals`,
    },
    {
      key: 'leave',
      label: KPI_LABELS.leave,
      value: formatNumber(s.onLeaveToday),
      foot: `${shareOf(s.onLeaveToday ?? 0, s.activeStaff)} of staff`,
    },
    {
      key: 'absent',
      label: KPI_LABELS.absent,
      value: formatNumber(s.absentToday),
      foot:
        live && (s.noShowsToday ?? 0) > 0 ? (
          <span className="font-medium text-[#d92d50] dark:text-rose-300">
            {formatNumber(s.noShowsToday)} no-show{s.noShowsToday === 1 ? '' : 's'} right now
          </span>
        ) : (
          `${shareOf(s.absentToday ?? 0, s.scheduledToday)} of scheduled`
        ),
    },
    live
      ? {
          key: 'clocked',
          label: KPI_LABELS.clocked,
          value: formatNumber(s.clockedInNow),
          foot: `${formatNumber(s.dueLaterToday)} more due later today`,
        }
      : {
          key: 'clocked',
          label: 'Scheduled',
          value: formatNumber(s.scheduledToday),
          foot: `${formatNumber(s.shiftsScheduledWeek)} shifts that week`,
        },
  ]
}

/**
 * The headline row: six numbers, each a door into its per-company breakdown.
 * When the viewer can read org payroll, this month's net pay takes the last
 * slot (the clocked-in count still leads the Recent clock-ins card).
 */
export function KpiStrip({
  data,
  isLoading,
  onOpen,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onOpen: (view: DashboardView) => void
}): React.ReactElement {
  const payroll = usePayrollTile()
  const tiles = useMemo(() => {
    const base = buildKpis(data)
    return payroll.model ? [...base.slice(0, 5), payroll.model] : base
  }, [data, payroll.model])

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tile) => (
        <KpiTile
          key={tile.key}
          model={tile}
          loading={tile.key === 'payroll' ? payroll.loading : isLoading}
          onOpen={() => onOpen(`kpi-${tile.key}`)}
        />
      ))}
    </div>
  )
}

interface KpiDetailSpec {
  title: string
  subtitle: string
  metricLabel: string
  value: (company: OrganizationDashboardCompany) => number
  /** Which daily series the week chart shows; omitted for metrics with no daily history. */
  daily?: 'present' | 'late' | 'absent' | 'onLeave'
}

const KPI_DETAIL: Record<Exclude<KpiKey, 'payroll'>, KpiDetailSpec> = {
  staff: {
    title: 'Total staff',
    subtitle:
      'Active members on every active company roster. A person in two companies counts once in the total and once per company below.',
    metricLabel: 'Members',
    value: (c) => c.memberCount,
  },
  present: {
    title: 'Present today',
    subtitle: 'Everyone who turned up today — on time, late or for a half day — by company.',
    metricLabel: 'Attended',
    value: (c) => (c.presentToday ?? 0) + (c.lateToday ?? 0) + (c.halfDayToday ?? 0),
    daily: 'present',
  },
  late: {
    title: 'Late arrivals',
    subtitle: 'People whose first clock-in today was past their shift start plus grace.',
    metricLabel: 'Late',
    value: (c) => c.lateToday ?? 0,
    daily: 'late',
  },
  leave: {
    title: 'On leave',
    subtitle: 'Approved full-day leave, on-leave shifts or on-leave attendance today.',
    metricLabel: 'On leave',
    value: (c) => c.onLeaveToday ?? 0,
    daily: 'onLeave',
  },
  absent: {
    title: 'Absent',
    subtitle: 'No-shows and missed shifts today. People with no shift are never counted as absent.',
    metricLabel: 'Absent',
    value: (c) => c.absentToday ?? 0,
    daily: 'absent',
  },
  clocked: {
    title: 'Clocked in now',
    subtitle: 'Open sessions right now, including overnight shifts that started yesterday.',
    metricLabel: 'Clocked in',
    value: (c) => c.clockedInNow ?? 0,
  },
}

function KpiWeekChart({
  data,
  series,
}: {
  data: OrganizationDashboard
  series: NonNullable<KpiDetailSpec['daily']>
}): React.ReactElement | null {
  // Memoized so a re-render doesn't replay the bar animation.
  const days = useMemo(
    () => data.weekAttendance.map((day) => ({ label: day.label, [series]: day[series] ?? 0 })),
    [data.weekAttendance, series]
  )
  const config = useMemo(() => ({ [series]: ATTENDANCE_SERIES[series] }), [series])
  if (!days.some((day) => (day[series] as number) > 0)) return null
  return (
    <div className="mb-6">
      <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">This week, by day</p>
      <ChartContainer config={config} className="aspect-auto h-40 w-full">
        <BarChart data={days} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={44} />
          <ChartTooltip
            cursor={{ fill: 'rgba(0,74,107,0.06)' }}
            content={<ChartTooltipContent />}
          />
          <Bar
            dataKey={series}
            fill={`var(--color-${series})`}
            radius={[4, 4, 0, 0]}
            maxBarSize={36}
          />
        </BarChart>
      </ChartContainer>
    </div>
  )
}

function CompanyMetricTable({
  companies,
  spec,
}: {
  companies: OrganizationDashboardCompany[]
  spec: KpiDetailSpec
}): React.ReactElement {
  const rows = [...companies]
    .map((company) => ({ company, value: spec.value(company) }))
    .sort((a, b) => b.value - a.value || a.company.name.localeCompare(b.company.name))
  const max = Math.max(1, ...rows.map((row) => row.value))
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_5rem]">
        <span>Company</span>
        <span className="hidden sm:block" aria-hidden />
        <span className="text-right">{spec.metricLabel}</span>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
        {rows.map(({ company, value }) => (
          <li
            key={company.companyId}
            className="grid grid-cols-[minmax(0,1fr)_5rem] items-center gap-4 px-4 py-2.5 text-sm sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_5rem]"
          >
            <Link
              to={`/companies/${company.companyId}`}
              className="truncate font-medium text-gray-900 hover:text-primary hover:underline dark:text-gray-100 dark:hover:text-sky-300"
            >
              {company.name}
            </Link>
            <span
              className="hidden h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700 sm:block"
              aria-hidden
            >
              <span
                className="block h-full rounded-full bg-primary/70 dark:bg-sky-400/70"
                style={{ width: `${(value / max) * 100}%` }}
              />
            </span>
            <span
              className={cn(
                'text-right font-medium tabular-nums',
                value === 0
                  ? 'text-gray-300 dark:text-gray-600'
                  : 'text-gray-900 dark:text-gray-100'
              )}
            >
              {formatNumber(value)}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
        <span>{formatNumber(rows.length)} companies</span>
        <span className="tabular-nums">Sum {formatNumber(total)}</span>
      </div>
    </div>
  )
}

function PayrollDetail(): React.ReactElement {
  const now = new Date()
  const { currency } = useOrgCurrency()
  const payroll = usePayrollDashboard(now.getFullYear(), now.getMonth() + 1)
  const preview = payroll.data?.finalizePreview?.totals
  const totals = preview && preview.employeeCount > 0 ? preview : undefined
  if (payroll.isLoading) {
    return <Bone className="h-28 w-full rounded-lg" />
  }
  if (!totals) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No payroll has been prepared for this month yet.{' '}
        <Link to="/payroll" className="font-medium text-primary hover:underline dark:text-sky-300">
          Open payroll
        </Link>
      </p>
    )
  }
  const figures = [
    { label: 'Net pay', value: formatCurrency(totals.netPay, currency) },
    { label: 'Gross pay', value: formatCurrency(totals.grossPay, currency) },
    { label: 'Tax', value: formatCurrency(totals.totalTax, currency) },
    { label: 'Employees', value: formatNumber(totals.employeeCount) },
    { label: 'Companies', value: formatNumber(totals.companyCount) },
  ]
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 dark:border-gray-700 dark:bg-gray-700 sm:grid-cols-5">
        {figures.map((figure) => (
          <div key={figure.label} className="bg-white px-4 py-3 dark:bg-gray-800">
            <dt className="text-xs text-gray-500 dark:text-gray-400">{figure.label}</dt>
            <dd className="mt-1 font-display text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50">
              {figure.value}
            </dd>
          </div>
        ))}
      </dl>
      {payroll.data?.nextAction && payroll.data.nextAction.type !== 'none' ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Next: {payroll.data.nextAction.label}.
          </span>{' '}
          {payroll.data.nextAction.description}
        </p>
      ) : null}
      <Link
        to="/payroll"
        className="inline-flex text-sm font-medium text-primary hover:underline dark:text-sky-300"
      >
        Open payroll
      </Link>
    </div>
  )
}

/** Detail modal for whichever KPI tile is open. */
export function KpiDetailModal({
  view,
  data,
  onClose,
}: {
  view: DashboardView | null
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  if (!view?.startsWith('kpi-')) return null
  const key = view.slice(4) as KpiKey
  if (key === 'payroll') {
    return (
      <DashboardDetailModal
        open
        onClose={onClose}
        title="Payroll this month"
        subtitle="Totals from this month's payroll batch, across every included company."
      >
        <PayrollDetail />
      </DashboardDetailModal>
    )
  }
  const spec = KPI_DETAIL[key]
  if (!spec) return null
  return (
    <DashboardDetailModal open onClose={onClose} title={spec.title} subtitle={spec.subtitle}>
      {!data ? (
        <Bone className="h-64 w-full rounded-lg" />
      ) : (
        <>
          {spec.daily ? <KpiWeekChart data={data} series={spec.daily} /> : null}
          <CompanyMetricTable companies={data.companyBreakdown} spec={spec} />
        </>
      )}
    </DashboardDetailModal>
  )
}
